/**
 * PromotionListContainer
 *
 *
 */
import React from 'react';
import MessageAlert from 'utils';
import PromotionListPresenter from './PromotionListPresenter';
import {
  promotionQueryKeys,
  useGetMapPromotions,
  useUpsertPromotion,
  useDeletePromotion,
} from 'hooks/PromotionHooks';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import Logger from 'utils/logger';
import Alert from 'utils/AlertManager';

const logger = new Logger('PromotionList');

/**----------------------------------------
 * [Component] 총관리자: 프로모션 목록 > 컨테이너
 * ----------------------------------------
 */
const INITIAL_VALUE = {
  start_date: dayjs().subtract(1, 'months').startOf('day'),
  end_date: dayjs().endOf('day'),
  value: 30,
};

const PromotionListContainer = (props) => {
  const {
    defaultShopId = null,
    // permission = 'ADMIN'
  } = props;

  const queryClient = useQueryClient();
  /* ===== INITIAL ===== */

  /* ===== STATE ===== */
  const [currentShop] = React.useState(defaultShopId ?? null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedList, setSelectedList] = React.useState([]);
  const [sortItems, setSortItems] = React.useState([]);
  const [isOpened, setIsOpened] = React.useState(false);
  const [dateValue, setDateValue] = React.useState(INITIAL_VALUE);
  const [period, setPeriod] = React.useState({
    start_date: dayjs().subtract(1, 'months').startOf('day').unix(),
    end_date: dayjs().endOf('day').unix(),
  });

  /* ===== VARIABLE ===== */
  /* ===== MUTATE ===== */
  const { upsertPromotion } = useUpsertPromotion();
  const { deletePromotion } = useDeletePromotion();

  const { data: Promotions } = useGetMapPromotions(
    {
      ...(currentShop && { shop_information_id: currentShop }),
      promotion_start_date: period.start_date,
      promotion_end_date: period.end_date,
      type: 'PRODUCT',
    },
    {
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
    }
  );

  const newPromotionList = React.useMemo(() => {
    return Promotions?.map((promotion) => {
      return {
        key: promotion.promotion_id,
        ...promotion,
        thumbnail_url: promotion.thumbnail_url ? promotion.thumbnail_url : '',
      };
    });
  }, [Promotions]);

  /* ===== FUNCTION ===== */
  /**
   * 데이터 저장 함수
   * ---
   * @param {*} t
   * @param {*} v
   * @returns
   */
  const handleSubmit = async (t, v) => {
    const type = t.toLocaleUpperCase();
    const value = v.toLocaleUpperCase();
    const message = {
      IS_SHOW: {
        Y: '선택한 프로모션를 목록에 노출 하시갰습니까?',
        N: '선택한 프로모션를 목록에 노출하지 않으시갰습니까?',
      },
      IS_MAIN: {
        Y: '선택한 프로모션를 메인에 노출 하시갰습니까?',
        N: '선택한 프로모션를 메인에 노출하지 않으시갰습니까?',
      },
      SORT: {
        Y: '정렬된 순서대로 수정하시겠습니까?',
      },
    };

    if (!window.confirm(message[type][value])) return;
    let success = false;
    let selectList = selectedList;
    try {
      setIsLoading(true);
      if (['IS_SHOW', 'IS_MAIN'].includes(type)) {
        selectList = selectList?.map((_item) => ({
          promotion_id: _item.promotion_id,
          [t]: v,
        }));
      } else {
        selectList = sortItems?.map((_item, index) => ({
          promotion_id: _item.promotion_id,
          order: index + 1,
        }));
      }
      const { status } = await upsertPromotion(selectList);

      if (status === 200) {
        MessageAlert.success(`성공적으로 프로모션이 수정되었습니다.`);
        success = true;
      }
    } catch (err) {
      console.error(`[UPDATE PROMOTION] ERROR : `, err);
      MessageAlert.error(`프로모션을 수정하는데 오류가 발생하였습니다.`);
      throw err;
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        if (success) {
          const queryKey = promotionQueryKeys.getPromotionsMapping({
            ...(currentShop && { shop_information_id: currentShop }),
            promotion_start_date: period.start_date,
            promotion_end_date: period.end_date,
            type: 'PRODUCT',
          });
          queryClient.invalidateQueries({ queryKey: queryKey.queryKey });
          setIsOpened(false);
        }
      }, 1000);
    }
  };

  /**
   * ANCHOR 프로모션 상품 삭제
   * --
   */
  const handleDelete = async () => {
    // 선택한 상품 없을 경우 예외 처리
    if (!selectedList.length) {
      MessageAlert.info('삭제하실 프로모션을 선택해주세요!');
      return;
    }

    // 상품 여러개 선택하였을 경우 예외 처리
    if (selectedList.length > 1) {
      MessageAlert.info('프로모션 삭제는 1건씩 가능합니다.');
      return;
    }

    if (
      !Alert.confirm(
        `프로모션을 정말 삭제하시겠습니까?\n삭제 후 복구 할 수 없습니다.`
      )
    )
      return;

    try {
      setIsLoading(true);

      const deleteItem = selectedList.at(0);

      if (!deleteItem) {
        MessageAlert.warning('해당 프로모션을 찾을 수 없습니다.');
        logger.warn('Not Found', '프로모션을 찾을 수 없음!');
        return;
      }

      const { promotion_id } = deleteItem;

      const { status, message } = await deletePromotion(promotion_id);

      if (status !== 200) {
        logger.warn('Delete Fail', message);
        MessageAlert.warning('프로모션 삭제에 실패하였습니다.');
        return;
      }

      logger.info('프로모션 상품 삭제 실행');
      MessageAlert.success('프로모션 상품이 정상적으로 삭제되었습니다.');

      // 캐싱 데이터 최신화
      const queryKey = promotionQueryKeys.getPromotionsMapping({
        ...(currentShop && { shop_information_id: currentShop }),
        promotion_start_date: period.start_date,
        promotion_end_date: period.end_date,
        type: 'PRODUCT',
      });
      queryClient.invalidateQueries({ queryKey: queryKey.queryKey });

      setSelectedList([]);
    } catch (err) {
      logger.error('Error!!', err);
      MessageAlert.error('프로모션 상품 삭제 중 에러가 발생하였습니다.');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  /* ===== HOOKS ===== */

  /* ===== RENDER ===== */
  // Properties
  const presenterProps = {
    setIsLoading,
    isLoading,
    promotionList: newPromotionList,
    selectedList,
    onSelect: setSelectedList,
    onSort: setSortItems,
    sortItems,
    isOpened,
    onOpen: setIsOpened,
    currentShop,
    dateValue,
    setDateValue,
    setPeriod,
    INITIAL_VALUE,
    onSubmit: handleSubmit,
    onDelete: handleDelete,
  };

  // JSX
  return (
    <>
      <PromotionListPresenter {...presenterProps} />
    </>
  );
};

/* DF */

export default PromotionListContainer;
